/**
 * ErrorBoundary — the last line of defence for a running session.
 *
 * Why this exists: the app renders through a single `createRoot(...).render(<App />)`.
 * Before this boundary, ANY throw during a render pass unmounted the entire tree.
 * Because `html, body { background: #000 }`, an unmounted tree is indistinguishable
 * from a deliberate blackout — the Director saw "the display keeps turning solid
 * black, and audio stops". The audio stopped because unmounting the tree tears down
 * the React-owned audio graph along with it.
 *
 * A trance session must never be destroyed by a rendering fault in one panel. This
 * boundary keeps the shell (and therefore the audio graph) mounted, shows a compact
 * recoverable notice, and offers a one-click reload. It deliberately does NOT
 * auto-reload: a mid-session reload would restart the induction from zero, which is
 * worse than a degraded panel.
 */
import React from "react";

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
        this._onReset = this._onReset.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        this.setState({ info });
        // Keep the fault visible in the console for diagnosis; never swallow it.
        console.error("[AceLabReact] render fault contained by ErrorBoundary", error, info);
        try {
            this.props.onError?.(error, info);
        } catch {
            /* reporting must never itself throw */
        }
    }

    _onReset() {
        this.setState({ error: null, info: null });
    }

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;

        const message = String(error?.message || error || "Unknown render fault");
        return (
            <div className="app-fault" role="alert">
                <div className="app-fault-card">
                    <h2 className="app-fault-title">A panel stopped responding</h2>
                    <p className="app-fault-body">
                        The session shell is still alive. Your audio graph was not torn down.
                    </p>
                    <pre className="app-fault-detail">{message}</pre>
                    <div className="app-fault-actions">
                        <button type="button" className="app-fault-button" onClick={this._onReset}>
                            Dismiss and continue
                        </button>
                        <button
                            type="button"
                            className="app-fault-button app-fault-button--ghost"
                            onClick={() => window.location.reload()}
                        >
                            Reload app
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
